import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useMe, type AccountSummary, type MeResponse } from './api'

interface AuthCtx {
  user: MeResponse['user'] | null
  account: AccountSummary | null
  isLoading: boolean
  refresh: () => Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, refetch } = useMe()
  const value = useMemo<AuthCtx>(
    () => ({
      user: data?.user ?? null,
      account: data?.account ?? null,
      isLoading,
      refresh: async () => {
        await refetch()
      },
    }),
    [data, isLoading, refetch],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth outside provider')
  return ctx
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()
  const loc = useLocation()
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-text2">Betöltés…</div>
    )
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />
  }
  return <>{children}</>
}
